import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectWatchConnected, selectWatchDrawerOpen, selectWatches } from '../../../redux/selector/watch'
import { setWatchDrawerOpen } from '../../../redux/action/watch'
import { deleteWatchThunk, fetchWatchesThunk, generatePairingCodeThunk } from '../../../redux/thunk/watch'
import getWatchPairingStatus from '../../../redux/axios/posNotification/watch/getWatchPairingStatus'
import { POLL_INTERVAL_MS, TOTAL_SECONDS } from './helper'

export const usePairing = () => {
	const dispatch = useDispatch()
	const drawerOpen = useSelector(selectWatchDrawerOpen)
	const isConnected = useSelector(selectWatchConnected)
	const watches = useSelector(selectWatches)

	const [drawerState, setDrawerState] = useState('not-connected')
	const [pairingCode, setPairingCode] = useState('')
	const [timeLeft, setTimeLeft] = useState(TOTAL_SECONDS)

	const pollRef = useRef(null)
	const timerRef = useRef(null)
	const isConnectedRef = useRef(isConnected)
	isConnectedRef.current = isConnected

	const stopPolling = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current)
			pollRef.current = null
		}
		if (timerRef.current) {
			clearInterval(timerRef.current)
			timerRef.current = null
		}
	}, [])

	useEffect(() => {
		if (!drawerOpen) return
		setDrawerState(isConnectedRef.current ? 'connected' : 'not-connected')
	}, [drawerOpen])

	useEffect(() => {
		if (drawerOpen) return
		stopPolling()
		setPairingCode('')
		setTimeLeft(TOTAL_SECONDS)
	}, [drawerOpen, stopPolling])

	useEffect(() => {
		if (drawerState !== 'code' || !pairingCode) return

		setTimeLeft(TOTAL_SECONDS)

		timerRef.current = setInterval(() => {
			setTimeLeft(prev => {
				if (prev <= 1) {
					stopPolling()
					setDrawerState('not-connected')
					setPairingCode('')
					return TOTAL_SECONDS
				}
				return prev - 1
			})
		}, 1000)

		pollRef.current = setInterval(async () => {
			try {
				const res = await getWatchPairingStatus(pairingCode)
				const status = res.data?.status
				if (status === 'confirmed') {
					stopPolling()
					setDrawerState('connecting')
					dispatch(fetchWatchesThunk()).then(() => setDrawerState('connected'))
				} else if (status === 'expired') {
					stopPolling()
					setDrawerState('not-connected')
					setPairingCode('')
				}
			} catch {
				setDrawerState('not-connected')
			}
		}, POLL_INTERVAL_MS)

		return stopPolling
	}, [drawerState, pairingCode, dispatch, stopPolling])

	const close = useCallback(() => {
		stopPolling()
		dispatch(setWatchDrawerOpen(false))
		setPairingCode('')
		setTimeLeft(TOTAL_SECONDS)
	}, [dispatch, stopPolling])

	const generate = useCallback(async () => {
		setDrawerState('generating')
		const result = await dispatch(generatePairingCodeThunk())
		if (result?.code) {
			setPairingCode(result.code)
			setDrawerState('code')
		} else {
			setDrawerState('not-connected')
		}
	}, [dispatch])

	const cancelCode = useCallback(() => {
		stopPolling()
		setPairingCode('')
		setDrawerState('not-connected')
	}, [stopPolling])

	const copyCode = useCallback(() => {
		if (pairingCode) {
			navigator.clipboard.writeText(pairingCode).catch(() => {})
		}
	}, [pairingCode])

	const askDelete = useCallback(() => setDrawerState('delete-confirm'), [])

	const confirmDelete = useCallback(async () => {
		const watchId = watches[0]?.watchId
		if (!watchId) return
		setDrawerState('deleting')
		await dispatch(deleteWatchThunk(watchId))
		setDrawerState('deleted')
	}, [dispatch, watches])

	const cancelDelete = useCallback(() => setDrawerState('connected'), [])

	const reconnect = useCallback(() => setDrawerState('not-connected'), [])

	return {
		drawerOpen,
		drawerState,
		pairingCode,
		timeLeft,
		watch: watches[0],
		handlers: { close, generate, cancelCode, copyCode, askDelete, confirmDelete, cancelDelete, reconnect },
	}
}
